import React, { useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TicketGroupData } from '../types';

// Validation schema for the ticket group form
const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Group name is required')
    .min(2, 'Group name must be at least 2 characters')
    .max(100, 'Group name must be less than 100 characters'),
  isSlotRequired: Yup.boolean().required(),
  isActive: Yup.boolean().required(),
});

type TicketGroupModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (ticketGroup: TicketGroupData) => void;
  isLoading?: boolean;
  ticketGroup?: TicketGroupData;
};

const TicketGroupModal: React.FC<TicketGroupModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  isLoading = false,
  ticketGroup,
}) => {
  // Initialize formik with validation
  const validation = useFormik({
    initialValues: {
      name: ticketGroup?.groupName || '',
      isSlotRequired: ticketGroup?.isSlotRequired || false,
      isActive: ticketGroup?.status === "active" ? true : false,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const formData: TicketGroupData = {
        id: ticketGroup?.id || Date.now(),
        groupName: values.name,
        isSlotRequired: values.isSlotRequired,
        slugId: '', // Will be set by parent component
        status: values.isActive ? 'active' : 'inactive',
        isActive: values.isActive,
        createdAt: ticketGroup?.createdAt || '',
        updatedAt: ticketGroup?.updatedAt || '',
      };
      onSubmit(formData);
    }
  });

  // Reset form when modal opens/closes or ticket group changes
  useEffect(() => {
    if (!isOpen) {
      validation.resetForm();
    }
  }, [isOpen, ticketGroup]);

  return (
    <Modal modalClassName="quick-v2-glass-modal" isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <h4 className="mb-0">
          {ticketGroup ? 'Edit Ticket Group' : 'Add New Ticket Group'}
        </h4>
      </ModalHeader>
      <Form onSubmit={validation.handleSubmit}>
        <ModalBody className="px-4">
          {/* Group Name Field */}
          <FormGroup>
            <Label className="form-label">
              Group Name<span className="text-danger"> *</span>
            </Label>
            <Input
              name="name"
              type="text"
              placeholder="Enter group name"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.name}
              invalid={
                validation.touched.name &&
                !!validation.errors.name
              }
            />
            {validation.touched.name &&
              validation.errors.name ? (
              <FormFeedback type="invalid">
                {validation.errors.name}
              </FormFeedback>
            ) : null}
          </FormGroup>

          {/* Is Slot Required Field */}
          <FormGroup>
            <div className="d-flex align-items-center">
              <div className="form-check form-switch">
                <Input
                  name="isSlotRequired"
                  type="checkbox"
                  role="switch"
                  id="isSlotRequiredSwitch"
                  checked={validation.values.isSlotRequired}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                />
                <Label className="form-check-label ms-2" htmlFor="isSlotRequiredSwitch">
                  <strong>Is Slot Required</strong>
                </Label>
              </div>
            </div>
            <small className="text-muted mt-1 d-block">
              Enable this if tickets in this group require time slot selection
            </small>
          </FormGroup>

          {/* Status Field */}
          <FormGroup>
            <div className="d-flex align-items-center">
              <div className="form-check form-switch">
                <Input
                  name="isActive"
                  type="checkbox"
                  role="switch"
                  id="isActiveSwitch"
                  checked={validation.values.isActive}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                />
                <Label className="form-check-label ms-2" htmlFor="isActiveSwitch">
                  <strong>Active</strong>
                </Label>
              </div>
            </div>
            <small className="text-muted mt-1 d-block">
              Enable this to make the ticket group available for selection
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button 
            className="btn btn-secondary me-2" 
            color="secondary" 
            type="button" 
            onClick={toggle}
          >
            Cancel
          </Button>
          <Button 
            className="btn btn-primary" 
            color="primary" 
            type="submit" 
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : (ticketGroup ? "Update Group" : "Add Group")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default TicketGroupModal;
